import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCurrentUser } from '@bonusly/who-am-i';

import { Bonus } from '../lib/bonus';

export const useCounterText = (bonus: Bonus): string | null => {
	const user = useCurrentUser();
	const { t } = useTranslation('common');

	return useMemo(() => {
		if (!bonus.reactions.length) return null;

		let userHasReacted = false;

		// check and remove if user is present and return a set to remove duplicated
		const reactions = bonus.reactions.reduce<Set<string>>((reactionsSet, reaction) => {
			if (reaction.user_id === user.id) {
				userHasReacted = true;
			} else {
				// NOTE: reaction.user_name will be removed when the homepage redesign is fully enabled
				reactionsSet.add(reaction.user?.display_name ?? reaction.user_name);
			}
			return reactionsSet;
		}, new Set<string>());

		const [firstNameReacted, ...namesReacted] = Array.from(reactions);
		const onlyOneOtherReacted = firstNameReacted && !namesReacted.length;

		// the general condition with counter
		if (!userHasReacted)
			return t('intl_overflow_list', {
				names: [firstNameReacted],
				count: namesReacted.length,
				interpolation: { escapeValue: false },
			});

		// only you reacted
		if (!firstNameReacted) return t('you');

		// you and 1 other reacted
		if (onlyOneOtherReacted) return t('intl_list', { names: [t('you'), firstNameReacted] });

		// you and many others reacted (first reacted included in the + 1)
		return t('intl_overflow_list', {
			names: [t('you')],
			count: namesReacted.length + 1,
		});
	}, [bonus, user, t]);
};
